import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Download } from "lucide-react";

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export default function ImportData() {
  const [entity, setEntity] = useState("clients");
  const [fields, setFields] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Velden dynamisch ophalen uit het datamodel (altijd actueel)
  useEffect(() => {
    api.get("/import/fields", { params: { entity } })
      .then((r) => setFields(r.data.fields || []))
      .catch(() => { setFields([]); toast.error("Kon importvelden niet laden"); });
  }, [entity]);

  const autoMap = (cols, flds) => {
    const m = {};
    for (const f of flds) {
      const syn = f.syn || [f.key];
      const hit = cols.find((c) => syn.some((s) => norm(c) === norm(s))) || cols.find((c) => syn.some((s) => norm(c).includes(norm(s)) && norm(s).length > 2));
      if (hit) m[f.key] = hit;
    }
    return m;
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "" });
      if (aoa.length < 1) { toast.error("Leeg bestand"); return; }
      const cols = aoa[0].map((c) => String(c).trim()).filter(Boolean);
      const dataRows = aoa.slice(1).map((r) => { const o = {}; cols.forEach((c, i) => { o[c] = r[i]; }); return o; }).filter((o) => Object.values(o).some((v) => String(v).trim()));
      setColumns(cols);
      setRows(dataRows);
      setMapping(autoMap(cols, fields));
      toast.success(`${dataRows.length} rijen ingelezen`);
    } catch (err) {
      toast.error("Kon bestand niet lezen");
    }
  };

  const buildRows = () => rows.map((r) => {
    const o = {};
    for (const f of fields) { const col = mapping[f.key]; if (col) o[f.key] = r[col]; }
    return o;
  });

  const doImport = async () => {
    if (!fields.filter((f) => f.required).every((f) => mapping[f.key])) {
      toast.error("Koppel alle verplichte velden"); return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/import/${entity}`, { rows: buildRows() });
      setResult(r.data);
      toast.success(`${r.data.created} van ${r.data.total} geïmporteerd`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import mislukt"); }
    finally { setBusy(false); }
  };

  const reset = () => { setColumns([]); setRows([]); setMapping({}); setResult(null); };

  const nameFor = (v) => ({ clients: "opdrachtgevers", tenants: "debiteuren", properties: "panden" }[v]);
  const sheetName = { clients: "Opdrachtgevers", tenants: "Debiteuren", properties: "Panden" };
  const downloadTemplate = () => {
    if (!fields.length) return;
    const headers = fields.map((f) => f.label);
    const example = fields.map((f) => (f.example ?? ""));
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    ws["!cols"] = headers.map(() => ({ wch: 24 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName[entity]);
    XLSX.writeFile(wb, `import-sjabloon-${nameFor(entity)}.xlsx`);
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Importeren</h1>
        <p className="text-slate-500">Vul opdrachtgevers of debiteuren in één keer vanuit een Excel- of CSV-bestand — zonder inklopwerk.</p>
      </div>

      <Card className="p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-3">
            {[["clients", "Opdrachtgevers"], ["properties", "Panden"], ["tenants", "Debiteuren"]].map(([v, l]) => (
              <button key={v} onClick={() => { setEntity(v); reset(); }} data-testid={`import-entity-${v}`}
                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${entity === v ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                {l}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="import-download-template"><Download className="h-4 w-4 mr-2" /> Download voorbeeldbestand</Button>
        </div>
        <label className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-xl p-6 cursor-pointer hover:border-blue-400 transition-colors" data-testid="import-dropzone">
          <Upload className="h-6 w-6 text-slate-400" />
          <div>
            <div className="font-medium text-slate-700">Kies een .xlsx of .csv bestand</div>
            <div className="text-xs text-slate-400">De eerste rij moet kolomkoppen bevatten.</div>
          </div>
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
        </label>
      </Card>

      {columns.length > 0 && !result && (
        <>
          <Card className="p-6 space-y-4" data-testid="import-mapping">
            <h2 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" /> Koppel kolommen ({rows.length} rijen)</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="text-sm text-slate-600">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                  <Combobox testId={`import-map-${f.key}`} value={mapping[f.key] || "none"} onChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "none" ? "" : v }))} placeholder="— Niet koppelen —" searchPlaceholder="Zoek kolom…" options={[{ value: "none", label: "— Niet koppelen —" }, ...columns.map((c) => ({ value: c, label: c }))]} />
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 space-y-3">
            <h2 className="font-heading font-semibold text-slate-800">Voorbeeld (eerste 5 rijen)</h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>{fields.filter((f) => mapping[f.key]).map((f) => <TableHead key={f.key}>{f.label}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {buildRows().slice(0, 5).map((r, i) => (
                    <TableRow key={i} data-testid={`import-preview-row-${i}`}>{fields.filter((f) => mapping[f.key]).map((f) => <TableCell key={f.key}>{String(r[f.key] ?? "")}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={reset} data-testid="import-cancel">Opnieuw</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doImport} disabled={busy} data-testid="import-commit">{busy ? "Bezig…" : `Importeer ${rows.length} rijen`}</Button>
            </div>
          </Card>
        </>
      )}

      {result && (
        <Card className="p-6 space-y-3" data-testid="import-result">
          <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-6 w-6" /> <span className="font-heading font-semibold text-lg">{result.created} van {result.total} geïmporteerd</span></div>
          {result.errors?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-amber-800 text-sm font-medium mb-1"><AlertTriangle className="h-4 w-4" /> {result.errors.length} rij(en) overgeslagen</div>
              <ul className="text-xs text-amber-700 list-disc pl-5 max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => <li key={i}>Rij {e.row}: {e.error}</li>)}
              </ul>
            </div>
          )}
          <Button variant="outline" onClick={reset} data-testid="import-again">Nog een bestand importeren</Button>
        </Card>
      )}
    </div>
  );
}
